/**
 * city_names.ts — default display names for the Commons settlements.
 *
 * The procedural settlement sites (shared/settlements.ts) carry only id/tier/biome,
 * no names. These deterministic defaults make `/list-cities` + `/tp-city <name>`
 * work day-one; admin-set persisted names (Phase 4 editor) arrive via the
 * `settlementName` SpacetimeDB table and are preferred when present.
 */
import type { SettlementSite } from '../../../shared/settlements.js';

// Evocative sci-fi settlement names, indexed by site id (14 sites, 22 names → unique).
const CITY_NAMES = [
  'Aethon', 'Novaris', 'Cindergate', 'Halcyon', 'Vesper', 'Solace', 'Ironmoor', 'Kelvin',
  'Meridian', 'Ashfall', 'Lumen', 'Tycho', 'Duneward', 'Verdance', 'Mycelia', 'Basalt',
  'Cryohaven', 'Zephyr', 'Cobalt', 'Perihelion', 'Umbra', 'Sable',
];

/** Deterministic default display name for a settlement site. */
export function cityName(site: SettlementSite): string {
  return CITY_NAMES[((site.id % CITY_NAMES.length) + CITY_NAMES.length) % CITY_NAMES.length];
}

/**
 * Returns the admin-set persistent name for a settlement site (from the
 * `settlementName` SpacetimeDB table) when one has been set, otherwise falls
 * back to the deterministic default `cityName(site)`.
 *
 * Accepts the raw `atlas.conn` value (typed as `unknown` here to avoid a
 * structural-compatibility fight with the generated DbConnection type, whose
 * `db` property is a strongly-typed view that doesn't nominally include the
 * Phase-4 `settlementName` table until after `spacetime generate` runs).
 * The access is done via safe runtime property lookups so pre-publish clients
 * simply fall through to the deterministic default.
 */
export function resolvedCityName(
  site: SettlementSite,
  conn: unknown,
): string {
  if (conn !== null && conn !== undefined && typeof conn === 'object') {
    const db = (conn as Record<string, unknown>)['db'];
    if (db !== null && db !== undefined && typeof db === 'object') {
      const tbl = (db as Record<string, unknown>)['settlementName'] as
        | { iter(): Iterable<{ siteId: bigint; name: string }> }
        | undefined;
      if (tbl && typeof tbl.iter === 'function') {
        const id = BigInt(site.id);
        for (const row of tbl.iter()) {
          if (row.siteId === id) return row.name;
        }
      }
    }
  }
  return cityName(site);
}
